import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function MapClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng);
    },
  });
  return null;
}

function MapResizer() {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 100);
  }, [map]);
  return null;
}

function LocationPicker({ lat, lng, onChange, label }) {
  const [localLat, setLocalLat] = useState(lat ?? '');
  const [localLng, setLocalLng] = useState(lng ?? '');

  useEffect(() => {
    setLocalLat(lat ?? '');
    setLocalLng(lng ?? '');
  }, [lat, lng]);

  const handleMapClick = (latlng) => {
    setLocalLat(latlng.lat.toFixed(7));
    setLocalLng(latlng.lng.toFixed(7));
    onChange(latlng.lat, latlng.lng);
  };

  const handleLatChange = (val) => {
    setLocalLat(val);
    const latNum = parseFloat(val);
    const lngNum = parseFloat(localLng);
    if (!isNaN(latNum) && !isNaN(lngNum)) {
      onChange(latNum, lngNum);
    }
  };

  const handleLngChange = (val) => {
    setLocalLng(val);
    const latNum = parseFloat(localLat);
    const lngNum = parseFloat(val);
    if (!isNaN(latNum) && !isNaN(lngNum)) {
      onChange(latNum, lngNum);
    }
  };

  const handleClear = () => {
    setLocalLat('');
    setLocalLng('');
    onChange(null, null);
  };

  const hasCoords = localLat !== '' && localLng !== '' && !isNaN(parseFloat(localLat)) && !isNaN(parseFloat(localLng));
  const center = hasCoords
    ? [parseFloat(localLat), parseFloat(localLng)]
    : [38.5, -121.5]; // Default: Sacramento area

  // Geocoding placeholder - uses Nominatim for reverse geocoding
  const [placeName, setPlaceName] = useState('');
  const debounceRef = useRef(null);

  const searchPlace = async (query) => {
    if (!query.trim()) return;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
      const data = await res.json();
      if (data.length > 0) {
        const { lat: rlat, lon: rlng, display_name } = data[0];
        setLocalLat(parseFloat(rlat).toFixed(7));
        setLocalLng(parseFloat(rlng).toFixed(7));
        setPlaceName(display_name);
        onChange(parseFloat(rlat), parseFloat(rlng));
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-xs font-medium text-gray-700">{label}</label>}

      {/* City search */}
      <div className="flex gap-2">
        <input
          type="text"
          value={placeName}
          onChange={(e) => {
            setPlaceName(e.target.value);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => searchPlace(e.target.value), 500);
          }}
          placeholder="Search city or place..."
          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        {hasCoords && (
          <button type="button" onClick={handleClear} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded" title="Clear coordinates">
            ✕
          </button>
        )}
      </div>

      {/* Map */}
      <div className="h-40 rounded-lg overflow-hidden border border-gray-200">
        <MapContainer center={center} zoom={hasCoords ? 10 : 5} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }} className="z-0">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {hasCoords && (
            <Marker position={[parseFloat(localLat), parseFloat(localLng)]} />
          )}
          <MapClickHandler onClick={handleMapClick} />
          <MapResizer />
        </MapContainer>
      </div>

      {/* Coordinate inputs */}
      <div className="flex gap-2">
        <div className="flex-1">
          <input type="text" value={localLat} onChange={(e) => handleLatChange(e.target.value)}
            placeholder="Latitude" className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <div className="flex-1">
          <input type="text" value={localLng} onChange={(e) => handleLngChange(e.target.value)}
            placeholder="Longitude" className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
      </div>
    </div>
  );
}

export default LocationPicker;